/**
 * AgentEditorModal - Full-screen modal for creating/editing agents.
 * 
 * Features:
 * - Full-screen layout with 3 columns
 * - Model selection, RAG settings, knowledge base
 * - Icon upload
 * - System prompt override (admin only)
 * 
 * @accessibility WCAG 2.1 AA (focus trap, ARIA, keyboard nav)
 */
import React, { useState, useCallback, useEffect, useId } from 'react'
import { X, Save, Loader2, AlertTriangle, Info, Upload, Brain, BookOpen, Settings, Sparkles, Users } from 'lucide-react'
import { api, systemPromptsApi, SystemPromptListItem } from '../lib/api'
import { HelpTooltip } from './Tooltip'

// =============================================================================
// Types
// =============================================================================

type Subject = { id: number; title: string; source: string }

interface AgentFormData {
  name: string
  subtitle: string
  instructions: string
  messageLimit: string
  modelId: string
  topK: string
  rerankTopN: string
  disableOwnKnowledge: boolean
  subjectIds: number[]
  teamId: number | null
  systemPromptId: number | null
}

interface AgentData {
  id: number
  name: string
  slug: string
  subtitle?: string | null
  instructions?: string | null
  message_limit?: number | null
  model_id?: string | null
  top_k?: number | null
  rerank_top_n: number
  disable_own_knowledge: boolean
  is_active: boolean
  subject_ids: number[]
  team_id?: number | null
  system_prompt_id?: number | null
}

interface AgentTeam {
  id: number
  title: string
}

interface AgentEditorModalProps {
  agent: AgentData | null  // null = create mode
  subjects: Subject[]
  subjectsLoading: boolean
  ollamaModels: string[]
  ollamaModelsLoading: boolean
  isAdmin: boolean
  systemPrompts: SystemPromptListItem[]
  teams: AgentTeam[]
  teamsLoading: boolean
  onClose: (saved: boolean) => void
  onSave: (agent: AgentData) => void
}

const DEFAULT_FORM: AgentFormData = {
  name: '',
  subtitle: '',
  instructions: '',
  messageLimit: '',
  modelId: '',
  topK: '10',
  rerankTopN: '5',
  disableOwnKnowledge: false,
  subjectIds: [],
  teamId: null,
  systemPromptId: null,
}

// =============================================================================
// Component
// =============================================================================

export default function AgentEditorModal({
  agent,
  subjects,
  subjectsLoading,
  ollamaModels,
  ollamaModelsLoading,
  isAdmin,
  systemPrompts,
  teams,
  teamsLoading,
  onClose,
  onSave,
}: AgentEditorModalProps) {
  const formId = useId()
  const isEditMode = !!agent

  // Form state
  const [formData, setFormData] = useState<AgentFormData>(() => {
    if (agent) {
      return {
        name: agent.name,
        subtitle: agent.subtitle || '',
        instructions: agent.instructions || '',
        messageLimit: agent.message_limit?.toString() || '',
        modelId: agent.model_id || '',
        topK: agent.top_k?.toString() || '10',
        rerankTopN: agent.rerank_top_n?.toString() || '5',
        disableOwnKnowledge: agent.disable_own_knowledge,
        subjectIds: agent.subject_ids || [],
        teamId: agent.team_id ?? null,
        systemPromptId: agent.system_prompt_id || null,
      }
    }
    return DEFAULT_FORM
  })

  const [iconFile, setIconFile] = useState<File | null>(null)
  const [iconPreview, setIconPreview] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Handle form changes
  const handleChange = useCallback((field: keyof AgentFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (error) setError(null)
  }, [error])

  // Handle subject toggle
  const handleSubjectToggle = useCallback((subjectId: number) => {
    setFormData(prev => ({
      ...prev,
      subjectIds: prev.subjectIds.includes(subjectId)
        ? prev.subjectIds.filter(id => id !== subjectId)
        : [...prev.subjectIds, subjectId]
    }))
  }, [])

  // Handle icon file change
  const handleIconChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setIconFile(file)
      const reader = new FileReader()
      reader.onloadend = () => setIconPreview(reader.result as string)
      reader.readAsDataURL(file)
    }
  }, [])

  // Submit handler
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.name.trim()) {
      setError('Le nom est requis')
      return
    }

    setSaving(true)
    setError(null)

    try {
      const payload: Record<string, any> = {
        name: formData.name.trim(),
        subtitle: formData.subtitle.trim() || null,
        instructions: formData.instructions.trim() || null,
        message_limit: formData.messageLimit ? parseInt(formData.messageLimit) : null,
        model_id: formData.modelId || null,
        top_k: formData.topK ? parseInt(formData.topK) : 10,
        rerank_top_n: formData.rerankTopN ? parseInt(formData.rerankTopN) : 5,
        disable_own_knowledge: formData.disableOwnKnowledge,
        subject_ids: formData.subjectIds,
      }

      // Team assignment (0 = remove from team)
      if (formData.teamId !== null) {
        payload.team_id = formData.teamId
      } else if (isEditMode && agent?.team_id) {
        // If editing and team was removed, send 0 to clear
        payload.team_id = 0
      }

      // Admin-only: system prompt override
      if (isAdmin && formData.systemPromptId !== null) {
        payload.system_prompt_id = formData.systemPromptId
      }

      let savedAgent: AgentData

      if (isEditMode && agent) {
        const { data } = await api.patch(`/agents/${agent.id}`, payload)
        savedAgent = data
      } else {
        const { data } = await api.post('/agents', payload)
        savedAgent = data
      }

      // Upload icon if selected
      if (iconFile && savedAgent.id) {
        const iconFormData = new FormData()
        iconFormData.append('file', iconFile)
        try {
          await api.post(`/agents/${savedAgent.id}/icon`, iconFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
        } catch (iconErr) {
          console.warn('Failed to upload icon:', iconErr)
        }
      }

      onSave(savedAgent)
      onClose(true)
    } catch (err: any) {
      const rawDetail = err?.response?.data?.detail
      // Handle object error responses (quota errors have {error, message, ...})
      const detail = typeof rawDetail === 'object' && rawDetail !== null
        ? rawDetail.message || rawDetail.error || JSON.stringify(rawDetail)
        : rawDetail || err?.message || 'Erreur lors de la sauvegarde'
      setError(detail)
    } finally {
      setSaving(false)
    }
  }, [formData, isEditMode, agent, iconFile, isAdmin, onSave, onClose])

  // ESC to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <div 
      className="fixed inset-0 z-50 bg-zinc-950"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${formId}-title`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800 bg-zinc-900">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-900/30">
            <Sparkles className="w-5 h-5 text-emerald-400" />
          </div>
          <h2 id={`${formId}-title`} className="text-lg font-semibold text-white">
            {isEditMode ? `Modifier: ${agent?.name}` : 'Nouvel agent'}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => onClose(false)}
            className="px-4 py-2 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            form={formId}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium transition-colors disabled:opacity-50"
            disabled={saving}
            data-onboarding={isEditMode ? 'agent-save-btn' : 'agent-create-btn'}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sauvegarde...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEditMode ? 'Mettre à jour' : 'Créer'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mx-6 mt-4 p-3 rounded-lg bg-red-900/20 border border-red-800 text-red-300 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Body - 3 columns */}
      <form 
        id={formId}
        onSubmit={handleSubmit}
        className="h-[calc(100vh-73px)] overflow-y-auto p-6"
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          
          {/* Column 1: Basic Info + Personality */}
          <div className="space-y-6">
            {/* Basic Info */}
            <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
              <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Informations générales
              </h3>
              
              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label htmlFor={`${formId}-name`} className="block text-sm font-medium text-zinc-300 mb-1">
                    Nom <span className="text-red-400">*</span>
                  </label>
                  <input
                    id={`${formId}-name`}
                    type="text"
                    value={formData.name}
                    onChange={e => handleChange('name', e.target.value)}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                    placeholder="Ex: Agent support"
                    required
                    data-onboarding="agent-name-input"
                  />
                </div>

                {/* Subtitle */}
                <div>
                  <label htmlFor={`${formId}-subtitle`} className="block text-sm font-medium text-zinc-300 mb-1">
                    Sous-titre
                  </label>
                  <input
                    id={`${formId}-subtitle`}
                    type="text"
                    value={formData.subtitle}
                    onChange={e => handleChange('subtitle', e.target.value)}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                    placeholder="Ex: Expert support Totemium"
                    data-onboarding="agent-subtitle-input"
                  />
                </div>

                {/* Icon */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    Icône
                  </label>
                  <div className="flex items-center gap-4">
                    {iconPreview ? (
                      <img src={iconPreview} alt="Preview" className="w-16 h-16 rounded-xl object-cover border border-zinc-700" />
                    ) : (
                      <div className="w-16 h-16 rounded-xl bg-zinc-800 border border-zinc-700 flex items-center justify-center">
                        <Upload className="w-6 h-6 text-zinc-500" />
                      </div>
                    )}
                    <label className="flex-1">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                        onChange={handleIconChange}
                        className="hidden"
                      />
                      <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors cursor-pointer text-sm">
                        <Upload className="w-4 h-4" />
                        Choisir une image
                      </span>
                    </label>
                  </div>
                </div>

                {/* Message limit */}
                <div>
                  <label htmlFor={`${formId}-limit`} className="block text-sm font-medium text-zinc-300 mb-1">
                    Limite de messages
                  </label>
                  <input
                    id={`${formId}-limit`}
                    type="number"
                    min={1}
                    value={formData.messageLimit}
                    onChange={e => handleChange('messageLimit', e.target.value)}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                    placeholder="Illimité si vide"
                  />
                  <p className="mt-1 text-xs text-zinc-500">Nombre max de messages par conversation</p>
                </div>
              </div>
            </section>

            {/* Personality */}
            <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
              <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                💬 Personnalité
              </h3>
              
              <div>
                <label htmlFor={`${formId}-instructions`} className="block text-sm font-medium text-zinc-300 mb-1">
                  Instructions système
                </label>
                <textarea
                  id={`${formId}-instructions`}
                  value={formData.instructions}
                  onChange={e => handleChange('instructions', e.target.value)}
                  rows={8}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 resize-none"
                  placeholder="Définissez le ton, le style et les comportements de votre assistant..."
                  data-onboarding="agent-instructions-input"
                />
                <p className="mt-1 text-xs text-zinc-500">
                  Ces instructions guident le comportement de l'agent dans toutes ses réponses
                </p>
              </div>
            </section>
          </div>

          {/* Column 2: AI Settings */}
          <div className="space-y-6">
            {/* Model & RAG */}
            <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
              <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Brain className="w-4 h-4" />
                Intelligence artificielle
              </h3>
              
              <div className="space-y-4">
                {/* Model */}
                <div>
                  <label htmlFor={`${formId}-model`} className="block text-sm font-medium text-zinc-300 mb-1 flex items-center gap-1">
                    🧠 Modèle IA
                    <HelpTooltip content="Le modèle de langage utilisé pour générer les réponses" position="right" />
                  </label>
                  <select
                    id={`${formId}-model`}
                    value={formData.modelId}
                    onChange={e => handleChange('modelId', e.target.value)}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                    disabled={ollamaModelsLoading}
                  >
                    <option value="">Défaut (système)</option>
                    {ollamaModels.map(model => (
                      <option key={model} value={model}>{model}</option>
                    ))}
                  </select>
                </div>

                {/* Top K */}
                <div>
                  <label htmlFor={`${formId}-topk`} className="block text-sm font-medium text-zinc-300 mb-1 flex items-center gap-1">
                    Top K (RAG)
                    <HelpTooltip content="Nombre de chunks récupérés pour le contexte" position="right" />
                  </label>
                  <input
                    id={`${formId}-topk`}
                    type="number"
                    min={1}
                    max={50}
                    value={formData.topK}
                    onChange={e => handleChange('topK', e.target.value)}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                    placeholder="10"
                  />
                  <p className="mt-1 text-xs text-zinc-500">Défaut: 10 — Plus élevé = plus de contexte mais plus lent</p>
                </div>

                {/* Rerank Top N */}
                <div>
                  <label htmlFor={`${formId}-rerank`} className="block text-sm font-medium text-zinc-300 mb-1 flex items-center gap-1">
                    Rerank Top N
                    <HelpTooltip content="Résultats finaux après reranking sémantique" position="right" />
                  </label>
                  <input
                    id={`${formId}-rerank`}
                    type="number"
                    min={1}
                    max={20}
                    value={formData.rerankTopN}
                    onChange={e => handleChange('rerankTopN', e.target.value)}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                    placeholder="5"
                  />
                  <p className="mt-1 text-xs text-zinc-500">Défaut: 5 — Nombre de documents les plus pertinents gardés</p>
                </div>

                {/* Strict mode */}
                <div className="pt-2">
                  <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-zinc-700 hover:border-zinc-600 transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.disableOwnKnowledge}
                      onChange={e => handleChange('disableOwnKnowledge', e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-emerald-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-zinc-200 flex items-center gap-2">
                        🎯 Réponses strictes
                      </span>
                      <p className="text-xs text-zinc-500 mt-1">
                        L'assistant ne répondra QU'avec les informations de votre base de connaissances
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </section>

            {/* Team Assignment */}
            <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
              <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Équipe
              </h3>
              
              <div>
                <label htmlFor={`${formId}-team`} className="block text-sm font-medium text-zinc-300 mb-1">
                  Assigner à une équipe
                </label>
                <select
                  id={`${formId}-team`}
                  value={formData.teamId ?? ''}
                  onChange={e => handleChange('teamId', e.target.value ? parseInt(e.target.value) : null)}
                  disabled={teamsLoading}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 disabled:opacity-50"
                  data-onboarding="agent-team-select"
                >
                  <option value="">Aucune équipe</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>{team.title}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-zinc-500">
                  Regroupez vos agents par équipe pour mieux les organiser
                </p>
              </div>
            </section>

            {/* Admin: System Prompt Override */}
            {isAdmin && (
              <section className="bg-zinc-900 border border-amber-800/50 rounded-xl p-5">
                <h3 className="text-sm font-medium text-amber-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                  🔧 Admin: System Prompt
                </h3>
                
                <div>
                  <label htmlFor={`${formId}-sysprompt`} className="block text-sm font-medium text-zinc-300 mb-1">
                    Override du prompt système
                  </label>
                  <select
                    id={`${formId}-sysprompt`}
                    value={formData.systemPromptId || ''}
                    onChange={e => handleChange('systemPromptId', e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  >
                    <option value="">Prompt par défaut</option>
                    {systemPrompts.map(sp => (
                      <option key={sp.id} value={sp.id}>{sp.name}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-amber-400/70">
                    Réservé admin — remplace le prompt système global
                  </p>
                </div>
              </section>
            )}
          </div>

          {/* Column 3: Knowledge Base */}
          <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 flex flex-col max-h-[calc(100vh-150px)]" data-onboarding="knowledge-tab">
            <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-4 flex items-center gap-2 flex-shrink-0">
              <BookOpen className="w-4 h-4" />
              Base de connaissances
            </h3>
            
            <p className="text-xs text-zinc-500 mb-4 flex-shrink-0">
              Sélectionnez les documents que l'agent peut consulter pour répondre aux questions
            </p>

            <div className="flex-1 overflow-y-auto pr-2 space-y-2" data-onboarding="knowledge-base-section">
              {subjectsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 text-zinc-500 animate-spin" />
                </div>
              ) : subjects.length === 0 ? (
                <div className="text-center py-8 text-zinc-500 text-sm">
                  <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Aucune base de connaissances disponible</p>
                  <p className="text-xs mt-1">Créez des sujets dans la section Documents</p>
                </div>
              ) : (
                subjects.map(subject => {
                  const checked = formData.subjectIds.includes(subject.id)
                  return (
                    <label
                      key={subject.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        checked 
                          ? 'bg-emerald-900/20 border-emerald-700' 
                          : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleSubjectToggle(subject.id)}
                        className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-emerald-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-zinc-200 truncate">{subject.title}</div>
                        <div className="text-xs text-zinc-500 truncate">{subject.source}</div>
                      </div>
                    </label>
                  )
                })
              )}
            </div>

            {/* Selected count */}
            <div className="mt-4 pt-4 border-t border-zinc-800 flex-shrink-0">
              <div className="flex items-center justify-between text-sm">
                <span className="text-zinc-400">Sélectionnés</span>
                <span className="font-medium text-emerald-400">
                  {formData.subjectIds.length} / {subjects.length}
                </span>
              </div>
            </div>
          </section>
          
        </div>
      </form>
    </div>
  )
}
